import * as React from "react";
import { useContext } from "react";
import { Formik, Form, Field } from "formik";
import styled from "styled-components";
import { validateField, formHasErrors } from "util/FormsUtils";
import { FormikFormGroup, Select, InputList, TimezoneSelect } from "components/common";
import { Alert, Button, ButtonToolbar, Row, Col, Panel, Input } from "components/bootstrap";
import { getPathnameWithoutId } from "util/URLUtils";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import useLocation from "routing/useLocation";
import { TELEMETRY_EVENT_TYPE } from "logic/telemetry/Constants";
import BackendWizardContext from "./BackendWizardContext";
export const STEP_KEY = "user-synchronization";
export const FORM_VALIDATION = {
  defaultRoles: { required: true },
  defaultUserTimezone: {},
  userFullNameAttribute: { required: true },
  userNameAttribute: { required: true },
  emailAttributes: {},
  userSearchBase: { required: true },
  userSearchPattern: { required: true },
  userUniqueIdAttribute: {}
};
const StyledInputList = styled(InputList)`
  margin: auto 15px;
`;
const UserSyncStep = ({
  help = {},
  excludedFields = {},
  formRef,
  onSubmit,
  onSubmitAll,
  submitAllError,
  validateOnMount,
  roles
}) => {
  const { setStepsState, ...stepsState } = useContext(BackendWizardContext);
  const { backendValidationErrors } = stepsState;
  const rolesOptions = roles.map((role) => ({ label: role.name, value: role.id })).toArray();
  const { pathname } = useLocation();
  const sendTelemetry = useSendTelemetry();
  const _onSubmitAll = (validateForm) => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.AUTHENTICATION.DIRECTORY_USER_SYNC_SAVE_CLICKED, {
      app_pathname: getPathnameWithoutId(pathname),
      app_section: "directory-service",
      app_action_value: "usersync-save"
    });
    validateForm().then((errors) => {
      if (!formHasErrors(errors)) {
        onSubmitAll();
      }
    });
  };
  const getInitalFormValues = (values) => ({
    ...values,
    ...!excludedFields.emailAttributes && { emailAttributes: values.emailAttributes || [] }
  });
  return /* @__PURE__ */ React.createElement(
    Formik,
    {
      initialValues: getInitalFormValues(stepsState.formValues),
      initialErrors: backendValidationErrors,
      innerRef: formRef,
      onSubmit,
      validateOnBlur: false,
      validateOnChange: false,
      validateOnMount
    },
    ({ isSubmitting, validateForm }) => /* @__PURE__ */ React.createElement(Form, { className: "form form-horizontal" }, /* @__PURE__ */ React.createElement(
      FormikFormGroup,
      {
        help: help.userSearchBase,
        label: "Search Base DN",
        error: backendValidationErrors == null ? void 0 : backendValidationErrors.userSearchBase,
        name: "userSearchBase",
        placeholder: "Search Base DN",
        validate: validateField(FORM_VALIDATION.userSearchBase)
      }
    ), /* @__PURE__ */ React.createElement(
      FormikFormGroup,
      {
        help: help.userSearchPattern,
        label: "Search Pattern",
        name: "userSearchPattern",
        error: backendValidationErrors == null ? void 0 : backendValidationErrors.userSearchPattern,
        placeholder: "Search Pattern",
        validate: validateField(FORM_VALIDATION.userSearchPattern)
      }
    ), /* @__PURE__ */ React.createElement(
      FormikFormGroup,
      {
        help: help.userNameAttribute,
        label: "Name Attribute",
        name: "userNameAttribute",
        error: backendValidationErrors == null ? void 0 : backendValidationErrors.userNameAttribute,
        placeholder: "Name Attribute",
        validate: validateField(FORM_VALIDATION.userNameAttribute)
      }
    ), !excludedFields.emailAttributes && /* @__PURE__ */ React.createElement(Field, { name: "emailAttributes", validate: validateField(FORM_VALIDATION.emailAttributes) }, ({ field: { name, value, onChange }, meta: { error } }) => /* @__PURE__ */ React.createElement(
      Input,
      {
        bsStyle: error ? "error" : void 0,
        help: help.emailAttributes,
        error: error != null ? error : backendValidationErrors == null ? void 0 : backendValidationErrors.emailAttributes,
        id: "email-attributes-input",
        label: "Email Attributes",
        labelClassName: "col-sm-3",
        wrapperClassName: "col-sm-9"
      },
      /* @__PURE__ */ React.createElement(
        StyledInputList,
        {
          id: "userEmailAttributes",
          placeholder: "Email Attributes",
          name,
          values: value,
          isClearable: true,
          onChange
        }
      )
    )), /* @__PURE__ */ React.createElement(
      FormikFormGroup,
      {
        help: help.userFullNameAttribute,
        label: "Full Name Attribute",
        name: "userFullNameAttribute",
        placeholder: "Full Name Attribute",
        error: backendValidationErrors == null ? void 0 : backendValidationErrors.userFullNameAttribute,
        validate: validateField(FORM_VALIDATION.userFullNameAttribute)
      }
    ), !excludedFields.userUniqueIdAttribute && /* @__PURE__ */ React.createElement(
      FormikFormGroup,
      {
        help: help.userUniqueIdAttribute,
        label: "ID Attribute",
        name: "userUniqueIdAttribute",
        placeholder: "ID Attribute",
        error: backendValidationErrors == null ? void 0 : backendValidationErrors.userUniqueIdAttribute,
        validate: validateField(FORM_VALIDATION.userUniqueIdAttribute)
      }
    ), /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { sm: 9, smOffset: 3 }, /* @__PURE__ */ React.createElement(Panel, { bsStyle: "info" }, "Changing the default role and time zone assignments will only affect new users created via", " ", stepsState.authBackendMeta.serviceTitle, "!"))), /* @__PURE__ */ React.createElement(Field, { name: "defaultRoles", validate: validateField(FORM_VALIDATION.defaultRoles) }, ({ field: { name, value, onChange, onBlur }, meta: { error } }) => /* @__PURE__ */ React.createElement(
      Input,
      {
        bsStyle: error ? "error" : void 0,
        help: help.defaultRoles,
        error: error != null ? error : backendValidationErrors == null ? void 0 : backendValidationErrors.defaultRoles,
        id: "default-roles-select",
        label: "Default Roles",
        labelClassName: "col-sm-3",
        wrapperClassName: "col-sm-9"
      },
      /* @__PURE__ */ React.createElement(
        Select,
        {
          multi: true,
          onBlur,
          onChange: (selectedRoles) => onChange({ target: { value: selectedRoles, name } }),
          options: rolesOptions,
          placeholder: "Search for roles",
          value
        }
      )
    )), /* @__PURE__ */ React.createElement(Field, { name: "defaultUserTimezone" }, ({ field: { name, value, onChange } }) => /* @__PURE__ */ React.createElement(
      Input,
      {
        id: "default-user-timezone-select",
        help: help.defaultUserTimezone,
        label: "Default User Time Zone",
        labelClassName: "col-sm-3",
        wrapperClassName: "col-sm-9"
      },
      /* @__PURE__ */ React.createElement(
        TimezoneSelect,
        {
          value: value || "Browser's time zone",
          name: "timezone",
          onChange: (newValue) => onChange({ target: { name, value: newValue } })
        }
      )
    )), /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { sm: 9, smOffset: 3 }, /* @__PURE__ */ React.createElement(Alert, { bsStyle: "info" }, "We recommend you test your user login in the sidebar panel to verify your settings."))), submitAllError, /* @__PURE__ */ React.createElement(ButtonToolbar, { className: "pull-right" }, /* @__PURE__ */ React.createElement(Button, { disabled: isSubmitting, onClick: () => _onSubmitAll(validateForm), type: "button" }, "Finish & Save Identity Service"), /* @__PURE__ */ React.createElement(
      Button,
      {
        bsStyle: "primary",
        disabled: isSubmitting,
        onClick: () => {
          sendTelemetry(TELEMETRY_EVENT_TYPE.AUTHENTICATION.DIRECTORY_NEXT_GROUP_SYNC_CLICKED, {
            app_pathname: getPathnameWithoutId(pathname),
            app_section: "directory-service",
            app_action_value: "groupsync-button"
          });
        },
        type: "submit"
      },
      "Next: Group Synchronization"
    )))
  );
};
export default UserSyncStep;
